import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Eye, Save, ImagePlus, Loader2,
  AlertCircle, CheckCircle, Sparkles, Building2, X, Star, MapPin,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import Shell from '../components/TopBarShell';
import MarkdownTextarea from '../components/MarkdownTextarea';
import { LT } from '../lib/appCatalogue';
import api from '../utils/api';
import { renderBody } from './Newsletters';

/* MANEVA · in-app careers CMS. Content editors (HR + flagged
 * teammates) draft and publish job openings here; jobs.lamptwist.be
 * reads the published rows from the public API. Each post belongs to
 * an Organization (LampTwist, Calum, …) that supplies the reusable
 * "About" blurb, logo and footer, and may name a contact person from
 * the team directory.
 *
 *   /admin/jobs      → list of openings (+ organizations manager)
 *   /admin/jobs/<id> → single-post editor (trilingual)
 *
 * Write access is gated by /api/announcements/me/is-hr/ via
 * api.canEditContent() · same gate as the newsletter CMS.
 */

export default function AdminJobs() {
  const { id } = useParams();
  const [allowed, setAllowed] = useState(undefined);  // undefined = loading
  useEffect(() => {
    api.canEditContent().then(setAllowed).catch(() => setAllowed(false));
  }, []);

  if (allowed === undefined) return <Shell><LoadingBar /></Shell>;
  if (!allowed) return <Shell><NotAllowed /></Shell>;
  if (id) return <JobEditor id={parseInt(id, 10)} />;
  return <JobsList />;
}

/* ── small shared atoms ──────────────────────────────────────────── */

const NotAllowed = () => (
  <div className="max-w-[520px] mt-10 rounded-2xl p-6"
       style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
    Cette page est réservée aux RH et éditeurs de contenu. Si tu pensais y avoir accès, ping un admin.
  </div>
);

const LoadingBar = () => (
  <div className="flex items-center gap-2 mt-10 text-[13px]" style={{ color: LT.green50 }}>
    <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
  </div>
);

const Field = ({ label, hint, children, required, className = '', compact = false }) => (
  // `compact` swaps the loud uppercase·letter-spaced caps label for a
  // calmer sentence-case label · used in the redesigned details block
  // so we have a clear visual hierarchy between section eyebrows and
  // the individual field labels nested under them.
  <label className={`flex flex-col gap-1 ${className}`}>
    <span className={compact
            ? 'text-[12px] font-medium'
            : 'text-[11px] uppercase tracking-[0.12em] font-num font-medium'}
          style={{ color: compact ? '#3a4f44' : LT.green50 }}>
      {label}{required && <span style={{ color: '#b54338' }}> *</span>}
    </span>
    {children}
    {hint && <span className="text-[11px]" style={{ color: LT.green50 }}>{hint}</span>}
  </label>
);

/* Segmented pill control · replaces a <select> when the option set
 * is small and stable. Reads as state, not as a form field. */
const Segmented = ({ value, onChange, options, allowEmpty = false }) => (
  <div className="inline-flex flex-wrap gap-1 rounded-xl p-1"
       style={{ background: '#fff', border: `1px solid ${LT.green30}` }}>
    {Object.entries(options).map(([v, label]) => {
      if (!allowEmpty && v === '') return null;
      const active = String(value) === String(v);
      return (
        <button key={v || 'none'} type="button" onClick={() => onChange(v)}
                className="px-2.5 py-1 rounded-lg text-[12.5px] transition"
                style={{
                  background: active ? LT.greenDeep : 'transparent',
                  color: active ? '#fefefe' : LT.greenDeep,
                  fontWeight: active ? 600 : 400,
                }}>
          {label}
        </button>
      );
    })}
  </div>
);

/* Lightweight collapsible · used to hide rarely-touched advanced
 * fields (réf, postes, clôture, tags, cover image) by default so the
 * editor leads with the things you actually edit each time. */
const Disclosure = ({ label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)}
              className="inline-flex items-center gap-1 text-[12px] font-medium"
              style={{ color: LT.green50 }}>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {label}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

/* Section sub-block eyebrow · smaller and quieter than the card-level
 * eyebrow, used to group fields within a single rounded section. */
const SubEyebrow = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-[0.14em] font-medium font-num mb-2"
       style={{ color: LT.green50 }}>
    {children}
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    published: { bg: '#e5efe7', fg: '#3a6f4a', label: 'Publié' },
    draft:     { bg: '#fef3e2', fg: '#9c6515', label: 'Brouillon' },
    closed:    { bg: '#f0e9e8', fg: '#8a5a52', label: 'Clôturé' },
  };
  const s = map[status] || map.draft;
  return (
    <span className="text-[10.5px] uppercase tracking-[0.14em] font-medium px-2 py-1 rounded-md font-num"
          style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
};

/* ── trilingual helpers · FR is canonical (top-level fields), NL/EN
 * live in `translations.{nl,en}`. Identical mechanism to the
 * newsletter editor, kept local so this page is self-contained. ──── */
const LANGS = ['fr', 'nl', 'en'];
const LANG_LABEL = { fr: 'Français', nl: 'Nederlands', en: 'English' };
const JOB_I18N_FIELDS = ['title', 'teaser', 'location', 'department', 'salary_range', 'schedule_label', 'description_markdown', 'profile_markdown', 'offer_markdown'];
const ORG_I18N_FIELDS = ['tagline', 'about'];

const EMPLOYMENT_LABELS = {
  flexi: 'Flexi-job', full_time: 'Temps plein', part_time: 'Temps partiel',
  student: 'Job étudiant', internship: 'Stage', freelance: 'Freelance',
  temporary: 'Intérim / temporaire', other: 'Autre',
};
const REMOTE_LABELS = { '': '—', on_site: 'Sur site', hybrid: 'Hybride', remote: 'Télétravail' };
const SOCIAL_KEYS = ['facebook', 'instagram', 'linkedin', 'pinterest', 'twitter'];

function localizedField(draft, lang, field) {
  if (lang === 'fr') return draft[field] || '';
  return ((draft.translations || {})[lang] || {})[field] || '';
}
function setLocalized(draft, lang, field, value) {
  if (lang === 'fr') return { ...draft, [field]: value };
  const next = { ...(draft.translations || {}) };
  next[lang] = { ...(next[lang] || {}), [field]: value };
  return { ...draft, translations: next };
}
function localizedFor(draft, fields) {
  const fr = {};
  fields.forEach(f => { fr[f] = draft?.[f] || ''; });
  return { ...(draft?.translations || {}), fr };
}
function tabStatusFor(translations, fields) {
  const out = {};
  for (const lang of LANGS) {
    const src = (translations && translations[lang]) || {};
    out[lang] = fields.some(f => (src[f] || '').trim() !== '');
  }
  return out;
}

const LangTabs = ({ value, onChange, status }) => (
  <div className="inline-flex items-center rounded-xl text-[12.5px]"
       style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
    {LANGS.map((lang, i) => {
      const active = value === lang;
      return (
        <button key={lang} type="button" onClick={() => onChange(lang)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 transition ${i > 0 ? 'border-l' : ''}`}
                style={{
                  background: active ? LT.greenDeep : 'transparent',
                  color: active ? '#fefefe' : LT.greenDeep,
                  borderColor: LT.green30, fontWeight: active ? 600 : 400,
                }}>
          <span>{LANG_LABEL[lang]}</span>
          {!active && (
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: status?.[lang] ? '#3d6b54' : LT.green30 }} />
          )}
        </button>
      );
    })}
  </div>
);

/* One-click translate · ships the FR copy to the shared Claude proxy
 * and drops the result into the active locale slot. */
const TranslateButton = ({ lang, fields, onResult }) => {
  const [busy, setBusy] = useState(false);
  const hasContent = Object.values(fields).some(v => (v || '').trim() !== '');
  const run = async () => {
    setBusy(true);
    try {
      const out = await api.adminTranslate('fr', lang, fields);
      onResult(out);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={run} disabled={busy || !hasContent}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium disabled:opacity-50"
            style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}
            title={`Traduire les champs FR en ${LANG_LABEL[lang]}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
      Tout traduire en {LANG_LABEL[lang]}
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * List page · job openings + an organizations manager (modal).
 * ──────────────────────────────────────────────────────────────── */
const JobsList = () => {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [orgModal, setOrgModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ organization: '', slug: '', title: '' });
  const [err, setErr] = useState('');

  const loadJobs = () => api.adminJobs()
    .then(rows => setItems(Array.isArray(rows) ? rows : (rows?.results || [])));
  const loadOrgs = () => api.adminOrganizations()
    .then(rows => setOrgs(Array.isArray(rows) ? rows : (rows?.results || [])));
  useEffect(() => { loadJobs(); loadOrgs(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const created = await api.adminCreateJob({
        organization: parseInt(form.organization, 10),
        slug: form.slug, title: form.title, status: 'draft',
      });
      nav(`/admin/jobs/${created.id}`);
    } catch (e2) {
      setErr(e2?.response?.data?.slug?.[0] || e2?.response?.data?.organization?.[0]
             || e2?.message || 'Création impossible');
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-medium font-num" style={{ color: LT.green50 }}>
            MANEVA · Recrutement
          </div>
          <h1 className="font-display text-[2rem] sm:text-[2.4rem] leading-tight mt-1" style={{ color: LT.greenDeep }}>
            Offres d'emploi
          </h1>
          <p className="mt-2 text-[14px] max-w-[680px]" style={{ color: '#3a4f44' }}>
            Crée, édite et publie les offres. Les offres publiées alimentent automatiquement
            la page carrières <span className="font-num">jobs.lamptwist.be</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOrgModal(true)}
                  className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-[13px] font-medium"
                  style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
            <Building2 className="h-4 w-4" /> Organisations
          </button>
          <button onClick={() => setCreating(v => !v)} disabled={orgs.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-medium disabled:opacity-50"
                  style={{ background: LT.greenDeep, color: '#fefefe' }}
                  title={orgs.length === 0 ? "Crée d'abord une organisation" : undefined}>
            <Plus className="h-4 w-4" /> Nouvelle offre
          </button>
        </div>
      </header>

      {orgs.length === 0 && items && (
        <div className="mb-6 rounded-2xl p-5 text-[13.5px] flex items-start gap-3"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: '#3a4f44' }}>
          <Building2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: LT.green50 }} />
          <span>
            Aucune organisation pour l'instant. Crée d'abord une entreprise (LampTwist, Calum…)
            via le bouton <strong>Organisations</strong> — elle fournira le logo, le texte « À propos »
            et le pied de page de chaque offre.
          </span>
        </div>
      )}

      {creating && orgs.length > 0 && (
        <form onSubmit={create}
              className="mb-6 rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
              style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
          <Field label="Organisation" required>
            <select required value={form.organization} className="ed-input"
                    onChange={e => setForm({ ...form, organization: e.target.value })}>
              <option value="">—</option>
              {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <Field label="Slug" hint="ex. flexi-job-order-picker" required>
            <input required value={form.slug} className="ed-input"
                   onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
          </Field>
          <Field label="Intitulé du poste" required className="md:col-span-2">
            <input required value={form.title} className="ed-input"
                   onChange={e => setForm({ ...form, title: e.target.value })} />
          </Field>
          {err && (
            <div className="md:col-span-4 text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}>
              <AlertCircle className="h-3.5 w-3.5" /> {err}
            </div>
          )}
          <div className="md:col-span-4 flex gap-2">
            <button type="submit" disabled={busy}
                    className="px-4 py-2 rounded-xl text-[13px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              {busy ? 'Création…' : 'Créer le brouillon'}
            </button>
            <button type="button" onClick={() => setCreating(false)}
                    className="px-4 py-2 rounded-xl text-[13px]" style={{ color: LT.green50 }}>
              Annuler
            </button>
          </div>
        </form>
      )}

      <section>
        {items === null && <LoadingBar />}
        {items && items.length === 0 && orgs.length > 0 && (
          <div className="rounded-2xl p-6 text-[14px]"
               style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
            Aucune offre encore — clique sur « Nouvelle offre » pour démarrer.
          </div>
        )}
        <ul className="space-y-2">
          {items?.map(job => (
            <li key={job.id}>
              <Link to={`/admin/jobs/${job.id}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition hover:-translate-y-px"
                    style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
                {job.organization?.logo
                  ? <img src={job.organization.logo} alt="" className="h-9 w-9 rounded-md object-contain bg-white shrink-0" style={{ border: `1px solid ${LT.green30}` }} />
                  : <span className="h-9 w-9 rounded-md grid place-items-center shrink-0" style={{ background: LT.yellow, color: LT.greenDeep }}><Building2 className="h-4 w-4" /></span>}
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-1.5">
                    {job.is_featured && <Star className="h-3 w-3 shrink-0" style={{ color: '#c79a3a', fill: '#c79a3a' }} />}
                    <span className="block font-display text-[1rem] leading-tight truncate" style={{ color: LT.greenDeep }}>
                      {job.title || '(sans titre)'}
                    </span>
                  </span>
                  <span className="block text-[11.5px] mt-0.5 font-num" style={{ color: LT.green50 }}>
                    {job.organization?.name || '—'}
                    {job.location ? ` · ${job.location}` : ''}
                    {' · '}{EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}
                  </span>
                </span>
                <StatusPill status={job.status} />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {orgModal && (
        <OrgManager orgs={orgs} onClose={() => setOrgModal(false)}
                    onChange={() => { loadOrgs(); loadJobs(); }} />
      )}
    </Shell>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Organizations manager · modal with a list + create/edit form. Few
 * rows expected, so an inline modal keeps it lightweight.
 * ──────────────────────────────────────────────────────────────── */
const blankOrg = () => ({
  name: '', slug: '', tagline: '', about: '', website: '', contact_email: '',
  address: '', map_url: '', socials: {}, translations: {}, is_active: true, sort_order: 0,
});

const OrgManager = ({ orgs, onClose, onChange }) => {
  const [editing, setEditing] = useState(null);  // org object or blank for new
  const [lang, setLang] = useState('fr');
  const [logoFile, setLogoFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const startNew = () => { setEditing(blankOrg()); setLogoFile(null); setLang('fr'); setErr(''); };
  const startEdit = (o) => { setEditing({ ...o, socials: o.socials || {}, translations: o.translations || {} }); setLogoFile(null); setLang('fr'); setErr(''); };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const payload = { ...editing };
      if (logoFile) payload.logo = logoFile; else delete payload.logo;
      delete payload.job_count; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.adminUpdateOrganization(editing.id, payload);
      else await api.adminCreateOrganization(payload);
      setEditing(null); setLogoFile(null);
      onChange();
    } catch (e) {
      setErr(e?.response?.data?.slug?.[0] || e?.message || 'Enregistrement impossible');
    } finally { setBusy(false); }
  };

  const remove = async (o) => {
    if (!window.confirm(`Supprimer l'organisation « ${o.name} » ? (impossible si des offres y sont rattachées)`)) return;
    try { await api.adminDeleteOrganization(o.id); onChange(); }
    catch (e) { window.alert(e?.response?.data?.detail || "Suppression impossible (offres rattachées ?)"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
         style={{ background: 'rgba(20,30,26,0.45)' }} onClick={onClose}>
      <div className="w-full max-w-[760px] my-8 rounded-2xl p-6" style={{ background: '#fff' }}
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-[1.5rem]" style={{ color: LT.greenDeep }}>Organisations</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg" style={{ color: LT.green50 }}><X className="h-5 w-5" /></button>
        </div>

        {!editing && (
          <>
            <ul className="space-y-2 mb-4">
              {orgs.map(o => (
                <li key={o.id} className="flex items-center gap-3 px-3 py-2 rounded-xl"
                    style={{ border: `1px solid ${LT.green30}` }}>
                  {o.logo
                    ? <img src={o.logo} alt="" className="h-8 w-8 rounded object-contain bg-white shrink-0" />
                    : <span className="h-8 w-8 rounded grid place-items-center shrink-0" style={{ background: LT.beigeLight, color: LT.green50 }}><Building2 className="h-4 w-4" /></span>}
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13.5px] font-medium truncate" style={{ color: LT.greenDeep }}>{o.name}</span>
                    <span className="block text-[11px] font-num" style={{ color: LT.green50 }}>{o.slug} · {o.job_count ?? 0} offre(s)</span>
                  </span>
                  <button onClick={() => startEdit(o)} className="ed-btn-ghost">Éditer</button>
                  <button onClick={() => remove(o)} className="ed-btn-ghost" style={{ color: '#b54338' }}><Trash2 className="h-3.5 w-3.5" /></button>
                </li>
              ))}
              {orgs.length === 0 && <li className="text-[13px]" style={{ color: LT.green50 }}>Aucune organisation.</li>}
            </ul>
            <button onClick={startNew}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-medium"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              <Plus className="h-4 w-4" /> Nouvelle organisation
            </button>
          </>
        )}

        {editing && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <LangTabs value={lang} onChange={setLang}
                        status={tabStatusFor(localizedFor(editing, ORG_I18N_FIELDS), ORG_I18N_FIELDS)} />
              {lang !== 'fr' && (
                <TranslateButton lang={lang}
                                 fields={{ tagline: editing.tagline || '', about: editing.about || '' }}
                                 onResult={out => setEditing(d => ({ ...d, translations: { ...(d.translations || {}), [lang]: { ...((d.translations || {})[lang] || {}), ...out } } }))} />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nom" required>
                <input className="ed-input" value={editing.name}
                       onChange={e => setEditing({ ...editing, name: e.target.value })} />
              </Field>
              <Field label="Slug" required hint="ex. lamptwist">
                <input className="ed-input" value={editing.slug}
                       onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
              </Field>
              <Field label={`Accroche (${LANG_LABEL[lang]})`} className="md:col-span-2">
                <input className="ed-input" value={localizedField(editing, lang, 'tagline')}
                       onChange={e => setEditing(setLocalized(editing, lang, 'tagline', e.target.value))} />
              </Field>
              <Field label={`À propos (${LANG_LABEL[lang]})`} hint="**gras** · *italique* · [texte](url)" className="md:col-span-2">
                <MarkdownTextarea rows={6} value={localizedField(editing, lang, 'about')}
                                  onChange={v => setEditing(setLocalized(editing, lang, 'about', v))} />
              </Field>
              <Field label="Logo">
                <div className="flex items-center gap-2">
                  {(logoFile || editing.logo) && (
                    <img src={logoFile ? URL.createObjectURL(logoFile) : editing.logo} alt=""
                         className="h-10 w-10 rounded object-contain bg-white" style={{ border: `1px solid ${LT.green30}` }} />
                  )}
                  <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer"
                         style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
                    <ImagePlus className="h-3.5 w-3.5" /> {logoFile ? logoFile.name : (editing.logo ? 'Remplacer' : 'Téléverser')}
                    <input type="file" accept="image/*" className="hidden"
                           onChange={e => setLogoFile(e.target.files[0] || null)} />
                  </label>
                </div>
              </Field>
              <Field label="Email de candidature">
                <input className="ed-input" type="email" value={editing.contact_email}
                       onChange={e => setEditing({ ...editing, contact_email: e.target.value })} />
              </Field>
              <Field label="Site web">
                <input className="ed-input" value={editing.website}
                       onChange={e => setEditing({ ...editing, website: e.target.value })} />
              </Field>
              <Field label="Adresse (siège)">
                <input className="ed-input" value={editing.address}
                       onChange={e => setEditing({ ...editing, address: e.target.value })} />
              </Field>
              <Field label="Lien Google Maps" className="md:col-span-2">
                <input className="ed-input" value={editing.map_url}
                       onChange={e => setEditing({ ...editing, map_url: e.target.value })} />
              </Field>
              {SOCIAL_KEYS.map(key => (
                <Field key={key} label={key}>
                  <input className="ed-input" value={editing.socials?.[key] || ''}
                         onChange={e => setEditing({ ...editing, socials: { ...(editing.socials || {}), [key]: e.target.value } })} />
                </Field>
              ))}
              <label className="flex items-center gap-2 text-[13px] md:col-span-2" style={{ color: LT.greenDeep }}>
                <input type="checkbox" checked={!!editing.is_active}
                       onChange={e => setEditing({ ...editing, is_active: e.target.checked })} />
                Active
              </label>
            </div>
            {err && <div className="text-[12.5px] flex items-center gap-2" style={{ color: '#b54338' }}><AlertCircle className="h-3.5 w-3.5" /> {err}</div>}
            <div className="flex gap-2 pt-1">
              <button onClick={save} disabled={busy || !editing.name || !editing.slug}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium disabled:opacity-50"
                      style={{ background: LT.greenDeep, color: '#fefefe' }}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
              </button>
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl text-[13px]" style={{ color: LT.green50 }}>
                Retour
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 * Single-post editor · shared metadata on top, trilingual content
 * below the language tabs, live preview on the side.
 * ──────────────────────────────────────────────────────────────── */
const JobEditor = ({ id }) => {
  const nav = useNavigate();
  const [draft, setDraft] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lang, setLang] = useState('fr');
  const [coverFile, setCoverFile] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const load = () => api.adminJob(id).then(job => {
    setCoverUrl(job.cover || null);
    setDraft({
      slug: job.slug || '', title: job.title || '', teaser: job.teaser || '',
      location: job.location || '', location_map_url: job.location_map_url || '',
      employment_type: job.employment_type || 'full_time', schedule_label: job.schedule_label || '',
      department: job.department || '', remote_policy: job.remote_policy || '',
      salary_range: job.salary_range || '', description_markdown: job.description_markdown || '',
      profile_markdown: job.profile_markdown || '', offer_markdown: job.offer_markdown || '',
      organization: job.organization?.id || '', contact_person: job.contact_person?.id || '',
      apply_email: job.apply_email || '', apply_url: job.apply_url || '',
      reference_code: job.reference_code || '', tags: job.tags || [],
      openings: job.openings ?? 1, is_featured: !!job.is_featured,
      closes_at: job.closes_at || '', status: job.status || 'draft',
      translations: job.translations || {},
    });
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.adminOrganizations().then(r => setOrgs(Array.isArray(r) ? r : (r?.results || [])));
    api.employees().then(r => setEmployees((Array.isArray(r) ? r : (r?.results || [])).filter(e => e.is_active !== false)));
  }, []);

  const set = (field, value) => setDraft(d => ({ ...d, [field]: value }));
  const selectedOrg = useMemo(() => orgs.find(o => String(o.id) === String(draft?.organization)), [orgs, draft?.organization]);
  const contact = useMemo(() => employees.find(e => String(e.id) === String(draft?.contact_person)), [employees, draft?.contact_person]);

  const save = async () => {
    setSaving(true); setErr(''); setSaved(false);
    try {
      const payload = { ...draft, organization: parseInt(draft.organization, 10) };
      payload.contact_person = draft.contact_person ? parseInt(draft.contact_person, 10) : null;
      payload.openings = parseInt(draft.openings, 10) || 1;
      payload.closes_at = draft.closes_at || null;
      if (coverFile) payload.cover = coverFile; else delete payload.cover;
      const updated = await api.adminUpdateJob(id, payload);
      setCoverFile(null);
      if (updated?.cover) setCoverUrl(updated.cover);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e?.response?.data?.slug?.[0] || e?.response?.data?.detail || e?.message || 'Enregistrement impossible');
    } finally { setSaving(false); }
  };

  const deleteJob = async () => {
    if (!window.confirm('Supprimer cette offre définitivement ?')) return;
    await api.adminDeleteJob(id);
    nav('/admin/jobs');
  };

  if (!draft) return <Shell><LoadingBar /></Shell>;

  const L = (f) => localizedField(draft, lang, f);
  const setL = (f, v) => setDraft(d => setLocalized(d, lang, f, v));

  return (
    <Shell>
      <Link to="/admin/jobs" className="inline-flex items-center gap-1.5 text-[13px] mb-3" style={{ color: LT.green50 }}>
        <ArrowLeft className="h-3.5 w-3.5" /> Toutes les offres
      </Link>

      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-medium font-num" style={{ color: LT.green50 }}>
            {selectedOrg?.name || 'Offre'} · {EMPLOYMENT_LABELS[draft.employment_type]}
          </div>
          <h1 className="font-display text-[1.8rem] sm:text-[2.2rem] leading-tight mt-1" style={{ color: LT.greenDeep }}>
            {draft.title || '(sans titre)'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={draft.status} />
          <button onClick={deleteJob}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12.5px] font-medium" style={{ color: '#b54338' }}>
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ── editor column ── */}
        <div className="space-y-5">
          {/* Shared metadata · split into 3 sub-blocks so the editor
              reads as identity → category → contact instead of one
              flat 13-field grid. Rare fields hide behind a disclosure. */}
          <section className="rounded-2xl p-5 space-y-5" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
            {/* Identité */}
            <div>
              <SubEyebrow>Identité</SubEyebrow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field compact label="Organisation" required>
                  <select className="ed-input" value={draft.organization}
                          onChange={e => set('organization', e.target.value)}>
                    <option value="">—</option>
                    {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Field>
                <Field compact label="Slug" required hint="URL unique de l'offre">
                  <input className="ed-input" value={draft.slug}
                         onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} />
                </Field>
              </div>
            </div>

            {/* Classification · pills instead of selects, status row
                doubles as the publication state. */}
            <div>
              <SubEyebrow>Classification</SubEyebrow>
              <div className="space-y-2.5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[12px] w-[88px] shrink-0" style={{ color: '#3a4f44' }}>Contrat</span>
                  <Segmented value={draft.employment_type}
                             onChange={v => set('employment_type', v)}
                             options={EMPLOYMENT_LABELS} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[12px] w-[88px] shrink-0" style={{ color: '#3a4f44' }}>Présence</span>
                  <Segmented value={draft.remote_policy}
                             onChange={v => set('remote_policy', v)}
                             options={REMOTE_LABELS} allowEmpty />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[12px] w-[88px] shrink-0" style={{ color: '#3a4f44' }}>Statut</span>
                  <Segmented value={draft.status}
                             onChange={v => set('status', v)}
                             options={{ draft: 'Brouillon', published: 'Publié', closed: 'Clôturé' }} />
                  <button type="button" onClick={() => set('is_featured', !draft.is_featured)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12.5px] transition"
                          style={{
                            background: draft.is_featured ? '#fef3e2' : 'transparent',
                            color: draft.is_featured ? '#9c6515' : LT.green50,
                            border: `1px solid ${draft.is_featured ? '#e9d3a3' : LT.green30}`,
                          }}
                          title="Mettre en avant sur la page carrières">
                    <Star className="h-3 w-3" style={{ fill: draft.is_featured ? '#c79a3a' : 'transparent', color: draft.is_featured ? '#c79a3a' : LT.green50 }} />
                    Mise en avant
                  </button>
                </div>
              </div>
            </div>

            {/* Candidature & lieu · how the candidate finds and reaches us */}
            <div>
              <SubEyebrow>Candidature & lieu</SubEyebrow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field compact label="Lien Google Maps du lieu">
                  <input className="ed-input" value={draft.location_map_url} onChange={e => set('location_map_url', e.target.value)} />
                </Field>
                <Field compact label="Personne de contact" hint="Membre de l'équipe">
                  <select className="ed-input" value={draft.contact_person} onChange={e => set('contact_person', e.target.value)}>
                    <option value="">—</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}{e.poste ? ` · ${e.poste}` : ''}</option>)}
                  </select>
                </Field>
                <Field compact label="Email de candidature" hint="Sinon, celui de l'organisation">
                  <input className="ed-input" type="email" value={draft.apply_email} onChange={e => set('apply_email', e.target.value)} />
                </Field>
                <Field compact label="Lien de candidature externe">
                  <input className="ed-input" value={draft.apply_url} onChange={e => set('apply_url', e.target.value)} />
                </Field>
              </div>
            </div>

            {/* Rare-touch fields tucked behind a disclosure */}
            <Disclosure label="Détails avancés">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <Field compact label="Réf." className="md:col-span-1">
                  <input className="ed-input" value={draft.reference_code} onChange={e => set('reference_code', e.target.value)} />
                </Field>
                <Field compact label="Postes" className="md:col-span-1">
                  <input className="ed-input" type="number" min="1" value={draft.openings} onChange={e => set('openings', e.target.value)} />
                </Field>
                <Field compact label="Clôture" className="md:col-span-2">
                  <input className="ed-input" type="date" value={draft.closes_at || ''} onChange={e => set('closes_at', e.target.value)} />
                </Field>
                <Field compact label="Tags" hint="séparés par des virgules" className="md:col-span-2">
                  <input className="ed-input" value={(draft.tags || []).join(', ')}
                         onChange={e => set('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
                </Field>
                <Field compact label="Image de couverture" className="md:col-span-6">
                  <div className="flex items-center gap-2">
                    {(coverFile || coverUrl) && (
                      <img src={coverFile ? URL.createObjectURL(coverFile) : coverUrl} alt=""
                           className="h-12 w-20 rounded-lg object-cover" style={{ border: `1px solid ${LT.green30}` }} />
                    )}
                    <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] cursor-pointer"
                           style={{ border: `1px solid ${LT.green30}`, color: LT.greenDeep, background: '#fff' }}>
                      <ImagePlus className="h-3.5 w-3.5" /> {coverFile ? coverFile.name : (coverUrl ? 'Remplacer' : 'Téléverser')}
                      <input type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files[0] || null)} />
                    </label>
                  </div>
                </Field>
              </div>
            </Disclosure>
          </section>

          {/* Trilingual content */}
          <section className="rounded-2xl p-5" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="text-[11px] uppercase tracking-[0.14em] font-medium" style={{ color: LT.green50 }}>
                Contenu ({LANG_LABEL[lang]})
              </div>
              <div className="flex items-center gap-2">
                {lang !== 'fr' && (
                  <TranslateButton lang={lang}
                                   fields={JOB_I18N_FIELDS.reduce((a, f) => ({ ...a, [f]: draft[f] || '' }), {})}
                                   onResult={out => setDraft(d => ({ ...d, translations: { ...(d.translations || {}), [lang]: { ...((d.translations || {})[lang] || {}), ...out } } }))} />
                )}
                <LangTabs value={lang} onChange={setLang}
                          status={tabStatusFor(localizedFor(draft, JOB_I18N_FIELDS), JOB_I18N_FIELDS)} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <Field label={`Intitulé (${LANG_LABEL[lang]})`} required={lang === 'fr'}>
                <input className="ed-input" value={L('title')} onChange={e => setL('title', e.target.value)} />
              </Field>
              <Field label={`Résumé court (${LANG_LABEL[lang]})`} hint="Affiché dans la liste des offres">
                <MarkdownTextarea rows={3} value={L('teaser')} onChange={v => setL('teaser', v)} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={`Lieu (${LANG_LABEL[lang]})`} hint="ex. Froyennes (Tournai)">
                  <input className="ed-input" value={L('location')} onChange={e => setL('location', e.target.value)} />
                </Field>
                <Field label={`Horaire (${LANG_LABEL[lang]})`} hint="ex. Lun–Ven, 9 h–17 h 30">
                  <input className="ed-input" value={L('schedule_label')} onChange={e => setL('schedule_label', e.target.value)} />
                </Field>
                <Field label={`Département / équipe (${LANG_LABEL[lang]})`}>
                  <input className="ed-input" value={L('department')} onChange={e => setL('department', e.target.value)} />
                </Field>
                <Field label={`Fourchette salariale (${LANG_LABEL[lang]})`}>
                  <input className="ed-input" value={L('salary_range')} onChange={e => setL('salary_range', e.target.value)} />
                </Field>
              </div>
              <Field label={`Descriptif du poste (${LANG_LABEL[lang]})`} hint="**gras** · *italique* · [texte](url) · ### sous-titre · - liste">
                <MarkdownTextarea rows={8} value={L('description_markdown')} onChange={v => setL('description_markdown', v)} />
              </Field>
              <Field label={`Profil recherché (${LANG_LABEL[lang]})`}>
                <MarkdownTextarea rows={7} value={L('profile_markdown')} onChange={v => setL('profile_markdown', v)} />
              </Field>
              <Field label={`Ce que nous offrons (${LANG_LABEL[lang]})`}>
                <MarkdownTextarea rows={7} value={L('offer_markdown')} onChange={v => setL('offer_markdown', v)} />
              </Field>
            </div>
          </section>

          <div className="flex items-center gap-3">
            <button onClick={save} disabled={saving || !draft.organization || !draft.title || !draft.slug}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-medium disabled:opacity-50"
                    style={{ background: LT.greenDeep, color: '#fefefe' }}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Enregistrer
            </button>
            {saved && <span className="inline-flex items-center gap-1.5 text-[12.5px]" style={{ color: '#3a6f4a' }}><CheckCircle className="h-3.5 w-3.5" /> Enregistré.</span>}
            {err && <span className="inline-flex items-center gap-1.5 text-[12.5px]" style={{ color: '#b54338' }}><AlertCircle className="h-3.5 w-3.5" /> {err}</span>}
          </div>
        </div>

        {/* ── preview column ── */}
        <div className="lg:sticky lg:top-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-medium mb-2 flex items-center gap-1.5" style={{ color: LT.green50 }}>
            <Eye className="h-3.5 w-3.5" /> Aperçu ({LANG_LABEL[lang]})
          </div>
          <JobPreview draft={draft} lang={lang} org={selectedOrg} contact={contact}
                      cover={coverFile ? URL.createObjectURL(coverFile) : coverUrl} />
        </div>
      </div>
    </Shell>
  );
};

/* Read-only preview · mirrors the public layout closely enough to
 * proof copy + structure. Markdown rendered with the newsletter
 * renderer for parity with what readers see. */
const JobPreview = ({ draft, lang, org, contact, cover }) => {
  const orgL = (f) => (lang === 'fr' ? (org?.[f] || '') : ((org?.translations || {})[lang] || {})[f] || org?.[f] || '');
  const L = (f) => localizedField(draft, lang, f);
  const badge = (txt) => txt
    ? <span className="text-[11px] px-2 py-0.5 rounded-full font-num" style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.green50 }}>{txt}</span>
    : null;
  return (
    <div className="rounded-2xl p-6 bg-white" style={{ border: `1px solid ${LT.green30}` }}>
      {cover && <img src={cover} alt="" className="w-full aspect-video object-cover rounded-lg mb-4" />}
      <h2 className="font-display text-[1.6rem] leading-tight" style={{ color: LT.greenDeep }}>
        {L('title') || '(sans titre)'}
      </h2>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {L('location') && (
          <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: LT.green50 }}>
            <MapPin className="h-3.5 w-3.5" /> {L('location')}
          </span>
        )}
        {badge(EMPLOYMENT_LABELS[draft.employment_type])}
        {badge(REMOTE_LABELS[draft.remote_policy] !== '—' ? REMOTE_LABELS[draft.remote_policy] : null)}
        {badge(L('schedule_label'))}
        {badge(L('department'))}
        {badge(L('salary_range'))}
      </div>

      {org && (orgL('about') || org.logo) && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-1.5">
            {org.logo && <img src={org.logo} alt="" className="h-6 object-contain" />}
            <h3 className="font-display text-[1.1rem]" style={{ color: LT.greenDeep }}>À propos de {org.name}</h3>
          </div>
          {orgL('tagline') && <p className="text-[13px] italic mb-1" style={{ color: LT.green50 }}>{orgL('tagline')}</p>}
          <div className="prose-sm text-[13.5px]" style={{ color: '#3a4f44' }}>{renderBody(orgL('about'))}</div>
        </div>
      )}

      <PreviewSection title="Descriptif du poste" body={L('description_markdown')} />
      <PreviewSection title="Profil recherché" body={L('profile_markdown')} />
      <PreviewSection title="Ce que nous offrons" body={L('offer_markdown')} />

      <div className="mt-5 pt-4 text-[13px]" style={{ borderTop: `1px solid ${LT.green30}`, color: '#3a4f44' }}>
        <div className="font-medium mb-1" style={{ color: LT.greenDeep }}>Candidature</div>
        {contact && <div>Contact · {contact.full_name}{contact.poste ? ` (${contact.poste})` : ''}</div>}
        <div className="font-num">{draft.apply_email || org?.contact_email || '—'}</div>
        {draft.apply_url && <div className="font-num truncate">{draft.apply_url}</div>}
      </div>
    </div>
  );
};

const PreviewSection = ({ title, body }) => {
  if (!body || !body.trim()) return null;
  return (
    <div className="mt-5">
      <h3 className="font-display text-[1.1rem] mb-1" style={{ color: LT.greenDeep }}>{title}</h3>
      <div className="text-[13.5px]" style={{ color: '#3a4f44' }}>{renderBody(body)}</div>
    </div>
  );
};
